import React, { useState, useCallback, useEffect, useRef } from 'react';
import { sendAgentChatMessage, endAgentChatSession, getAgentChat, getClosedChats } from 'actions/agentChat';
import ChatHeader from './ChatHeader';
import ChatUsageBar from './ChatUsageBar';
import ChatBody from './ChatBody';
import ChatHistory from './ChatHistory';
import ChatFooter from './ChatFooter';
import useRoxyState from './Roxy/useRoxyState';
import './RoxyPanel.scss';

const DEFAULT_CHAT_LIMIT = 20;

const mapResponseMessages = (messages) =>
    messages.map(msg => ({
        role: msg.sender === 'USER' ? 'user' : msg.sender === 'INTERNAL' ? 'internal' : 'agent',
        content: msg.message,
    }));

const RoxyPanel = ({ open, onClose, applicantId, applicationName, chatStatus, onRefreshChatStatus, creditCosts, creditUsageEnabled }) => {
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [quota, setQuota] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [isLimitReached, setIsLimitReached] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [hasFetchedChat, setHasFetchedChat] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [closedSessions, setClosedSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [viewingClosedChat, setViewingClosedChat] = useState(false);
    const sessionIdRef = useRef(null);
    const panelRef = useRef(null);

    const { state: roxyState, pulseSuccess, pulseAlert } = useRoxyState({
        open,
        loading,
        draft: message,
        messages,
        isLimitReached,
    });

    const chatUsage = quota?.used || 0;
    const chatLimit = quota?.total || DEFAULT_CHAT_LIMIT;

    const getChatCreditCost = () => {
        if (!creditCosts) return 0;
        const costObj = creditCosts.find(cost => cost.requestType === 'START_REPUTATION_AGENT_CHAT');
        return costObj ? costObj.creditCost : 0;
    };

    useEffect(() => {
        sessionIdRef.current = sessionId;
    }, [sessionId]);


    useEffect(() => {
        if (!open) return undefined;
        if (panelRef.current) panelRef.current.focus();
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    const resetState = useCallback(() => {
        setMessages([]);
        setSessionId(null);
        setQuota(null);
        setIsLimitReached(false);
        setIsDisabled(false);
        setSuggestions([]);
        setMessage('');
        setViewingClosedChat(false);
    }, []);

    useEffect(() => {
        if (!open) {
            setHasFetchedChat(false);
            return;
        }
        if (hasFetchedChat) return;

        if (!chatStatus || chatStatus.status === 'NONE') {
            resetState();
            setHasFetchedChat(true);
            return;
        }

        if (chatStatus.status === 'ENDED') {
            setIsDisabled(true);
            if (chatStatus.sessionId) setSessionId(chatStatus.sessionId);
            setHasFetchedChat(true);
        } else if (chatStatus.status === 'STARTED') {
            setIsDisabled(false);
            setSessionId(chatStatus.sessionId);
            setLoading(true);
            setHasFetchedChat(true);
            fetchChat(applicantId, chatStatus.sessionId);
        }
    }, [open, chatStatus, applicantId, hasFetchedChat, resetState]);

    const fetchChat = (appId, sessId) => {
        getAgentChat(appId, sessId)
            .then((response) => {
                if (response.messages?.length > 0) setMessages(mapResponseMessages(response.messages));
                if (response.quota) {
                    setQuota(response.quota);
                    setIsLimitReached(response.quota.available === 0);
                }
                if (response.chatSuggestions) setSuggestions(response.chatSuggestions);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    const handleSend = useCallback(async () => {
        if (messages.length > 0 && !message.trim()) return;

        const userMessage = message;
        setMessage('');
        if (userMessage.trim()) {
            setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        }
        setLoading(true);

        try {
            const response = await sendAgentChatMessage(applicantId, sessionIdRef.current, userMessage);
            setSessionId(response.sessionId);
            if (response.quota) {
                setQuota(response.quota);
                setIsLimitReached(response.quota.available === 0);
            }
            if (response.chatSuggestions) setSuggestions(response.chatSuggestions);
            if (response.messages?.length > 0) setMessages(mapResponseMessages(response.messages));
        } catch {
            pulseAlert();
            setMessages(prev => [...prev, { role: 'agent', content: 'Something went wrong. Please try again.' }]);
        } finally {
            setLoading(false);
        }
    }, [message, messages.length, applicantId, pulseAlert]);

    const handleEndConversation = useCallback(async () => {
        try {
            if (sessionIdRef.current) await endAgentChatSession(applicantId, sessionIdRef.current);
            pulseSuccess();
        } finally {
            resetState();
            setHasFetchedChat(false);
            onClose();
            if (onRefreshChatStatus) onRefreshChatStatus();
        }
    }, [applicantId, onClose, resetState, onRefreshChatStatus, pulseSuccess]);

    const handleViewHistory = () => {
        if (showHistory) {
            setViewingClosedChat(false);
            resetState();
            setHasFetchedChat(false);
        } else {
            setLoadingSessions(true);
            getClosedChats(applicantId)
                .then((res) => setClosedSessions(res || []))
                .catch(() => {})
                .finally(() => setLoadingSessions(false));
        }
        setShowHistory(!showHistory);
    };

    const handleSelectSession = (session) => {
        setLoading(true);
        setShowHistory(false);
        setIsDisabled(true);
        setViewingClosedChat(true);
        setSessionId(session.sessionId);
        fetchChat(applicantId, session.sessionId);
    };

    const showFooter = !showHistory && !viewingClosedChat;

    return (
        <div
            ref={panelRef}
            className={`roxy-panel${open ? ' roxy-panel--open' : ''}`}
            role="dialog"
            aria-label="Roxy"
            aria-hidden={!open}
            tabIndex={-1}
        >
            <ChatHeader
                applicationName={applicationName}
                sessionId={sessionId}
                roxyState={roxyState}
                hasClosedChats={chatStatus?.closedChats}
                showHistory={showHistory}
                onClose={onClose}
                onToggleHistory={handleViewHistory}
            />

            {!showHistory && (
                <ChatUsageBar usage={chatUsage} limit={chatLimit} />
            )}

            {showHistory ? (
                <ChatHistory
                    sessions={closedSessions}
                    loading={loadingSessions}
                    onSelectSession={handleSelectSession}
                />
            ) : (
                <ChatBody
                    messages={messages}
                    loading={loading}
                    suggestions={suggestions}
                    message={message}
                    isLimitReached={isLimitReached}
                    isDisabled={isDisabled}
                    onMessageChange={setMessage}
                    onSend={handleSend}
                    creditUsageEnabled={creditUsageEnabled}
                    creditCost={getChatCreditCost()}
                />
            )}

            {showFooter && (
                <ChatFooter
                    onEndConversation={handleEndConversation}
                    isDisabled={isDisabled}
                    hasMessages={messages.length > 0}
                />
            )}
        </div>
    );
};

export default RoxyPanel;
